<script setup>
import { computed } from "vue";
import { iconHref } from "~/utils/icons.js";

const STYLES = {
  danger: {
    button: "text-[var(--gl-feedback-danger-icon-color)] hover:text-[var(--gl-feedback-danger-text-color)]",
    container:
      "border-[var(--gl-feedback-danger-border-color)] bg-[var(--gl-feedback-danger-background-color)] text-[var(--gl-feedback-danger-text-color)]",
    icon: "error",
    iconClass: "text-[var(--gl-feedback-danger-icon-color)]"
  },
  info: {
    button: "text-[var(--gl-feedback-info-icon-color)] hover:text-[var(--gl-feedback-info-text-color)]",
    container:
      "border-[var(--gl-feedback-info-border-color)] bg-[var(--gl-feedback-info-background-color)] text-[var(--gl-feedback-info-text-color)]",
    icon: "information-o",
    iconClass: "text-[var(--gl-feedback-info-icon-color)]"
  },
  success: {
    button: "text-[var(--gl-feedback-success-icon-color)] hover:text-[var(--gl-feedback-success-text-color)]",
    container:
      "border-[var(--gl-feedback-success-border-color)] bg-[var(--gl-feedback-success-background-color)] text-[var(--gl-feedback-success-text-color)]",
    icon: "check-circle",
    iconClass: "text-[var(--gl-feedback-success-icon-color)]"
  },
  tip: {
    button: "text-[var(--gl-feedback-neutral-icon-color)] hover:text-[var(--gl-feedback-neutral-text-color)]",
    container:
      "border-[var(--gl-feedback-neutral-border-color)] bg-[var(--gl-feedback-neutral-background-color)] text-[var(--gl-feedback-neutral-text-color)]",
    icon: "bulb",
    iconClass: "text-[var(--gl-feedback-neutral-icon-color)]"
  },
  warning: {
    button: "text-[var(--gl-feedback-warning-icon-color)] hover:text-[var(--gl-feedback-warning-text-color)]",
    container:
      "border-[var(--gl-feedback-warning-border-color)] bg-[var(--gl-feedback-warning-background-color)] text-[var(--gl-feedback-warning-text-color)]",
    icon: "warning",
    iconClass: "text-[var(--gl-feedback-warning-icon-color)]"
  }
};

const { dismissLabel, dismissible, title, variant } = defineProps({
  dismissLabel: { default: "Dismiss", type: String },
  dismissible: { default: true, type: Boolean },
  title: { default: null, type: String },
  variant: {
    default: "info",
    type: String,
    validator: (v) => ["info", "warning", "danger", "success", "tip"].includes(v)
  }
});

const emit = defineEmits(["dismiss"]);

const style = computed(() => STYLES[variant] ?? STYLES.info);
</script>

<template>
  <div
    :class="['flex items-start gap-3 rounded border px-4 py-3 text-sm', style.container]"
    role="alert"
    data-testid="alert-message"
  >
    <svg :class="['mt-0.5 h-4 w-4 shrink-0', style.iconClass]" aria-hidden="true">
      <use :href="iconHref(style.icon)" />
    </svg>

    <div class="flex-1">
      <strong v-if="title" class="block">{{ title }}</strong>
      <slot />
    </div>

    <button
      v-if="dismissible"
      type="button"
      :aria-label="dismissLabel"
      :class="[
        'shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)]',
        style.button
      ]"
      @click="emit('dismiss')"
    >
      <svg class="h-4 w-4" aria-hidden="true">
        <use :href="iconHref('close')" />
      </svg>
    </button>
  </div>
</template>
